<template>
  <div class="border-rounded mt-3">
    <b-row no-gutters class="mb-3">
      <b-col cols="12">
        <div class="mb-3 text-muted">
          {{ $t('efficiencyTestDetails.scoringDefinitionDescription') }}
        </div>
      </b-col>
      <b-col cols="6">
        <div class="">
          <div class="mb-2">
            <span class="font-weight-bold">{{ $t('efficiencyTestDetails.summary.passThresholdPercent') }}</span>
          </div>
          <custom-slider :value.sync="passThresholdPercent" :min="0" :max="100" :interval="1" :tooltip="'none'">
          </custom-slider>
          <div class="d-flex justify-content-between">
            <span class="small text-muted">(0% - 100%)</span> <span class="">{{ passThresholdPercent }}%</span>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row no-gutters class="mt-3 mb-3" v-if="getModelsPermission">
      <b-col cols="6">
        <div class="d-flex flex-row">
          <div class="">
            <label for="name" v-t="'efficiencyTestDetails.associateModelLabel'"></label>
            <custom-multi-select
              maintain-original-order
              v-model="scoringProfileDefinitionSelectValue"
              :options="availableModels"
              :variant="'white'"
              :placeholder="$t('common.search')"
              :default-text="$t('efficiencyTestDetails.noAssociatedModel')"
            >
            </custom-multi-select>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- title -->
    <div>
      <!-- model definition (shown instead of the custom drivers editor when a model is picked) -->
      <template v-if="getModelsPermission && getSelectedModelId">
        <div class="">
          <b-row no-gutters class="mb-3">
            <b-col>
              <span class="user-guideline-text">
                {{ $t('efficiencyTests.dragModelDriversGuideline') }}
              </span>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <efficiency-tests-model-definition
                :driverFamiliesList="getUnsortedModelDrivers"
                :drivers="getSortedModelDrivers"
                :drivers-limit="maxDriversListAccepted"
                @on-move-driver="onMoveDriver"
                @on-reset-sorting="onResetSorting"
                @on-update-sorting="onUpdateSorting"
                @on-remove-model-driver="onRemoveModelDriver"
              ></efficiency-tests-model-definition>
            </b-col>
          </b-row>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import CustomTooltip from '../../components/Lib/CustomTooltip.vue'
import SimpleSelect from '../../components/Lib/SimpleSelect.vue'
import CustomSlider from '../../components/Lib/CustomSlider.vue'
import CustomMultiSelect from '../../components/Lib/CustomMultiSelect.vue'
import EfficiencyTestsDefinition from '../../components/EfficiencyTests/EfficiencyTestsDefinition.vue'
import EfficiencyTestsModelDefinition from '../../components/EfficiencyTests/EfficiencyTestsModelDefinition.vue'
import {
  calculateDriversWeightsLists,
  mapScoringDefinitionforModel,
  formatDriverWeightNumber,
} from '../../utils/drivers'
import { SCORING_DEFINITION_TYPES } from '../../settings/scoringProfilesSettings'
import { isNearlyEqual } from '../../utils/formatter'
import { createNamespacedHelpers } from 'vuex'
const { mapGetters, mapMutations, mapActions } = createNamespacedHelpers('efficiencyTestsDefinition')

export default {
  components: {
    CustomTooltip,
    CustomSlider,
    SimpleSelect,
    CustomMultiSelect,
    EfficiencyTestsDefinition,
    EfficiencyTestsModelDefinition,
  },

  data() {
    return {
      maxDriversListAccepted: 30,
    }
  },

  computed: {
    ...mapGetters([
      'getMaxTotalWeight',
      'getSelectedModelId',
      'getDriversIndicators',
      'getSelectedWeightType',
      'getSortedModelDrivers',
      'getSortedModelWeights',
      'getUnsortedModelDrivers',
      'getSequenceWeightsDecay',
      'getScoringDefinitionSettings',
      'getSelectedScoringDefinition',
      'getDriversIndicatorsCategories',
      'getDriversWeightsGreaterThanZero',
      'getScoringProfileDefinitionSelectValue',
      'getPassThresholdPercent',
    ]),

    isScoringDefinitionCustomConfig: {
      get() {
        return this.getScoringProfileDefinitionSelectValue === 0
      },
    },

    // Models are a shared catalog (scoringProfileModelId), not EfficiencyTests-specific — reused
    // from ScoringProfiles' own module rather than duplicated, same as searchNew.options.* enum
    // labels elsewhere.
    getModelsPermission: {
      get() {
        return this.$store.getters['can_ViewScoringProfileModels']
      },
    },

    // "Personalizzato" (custom scoring definition, value '0') removed from the options — a model is
    // now always required, so the multiselect only ever offers real models.
    availableModels: {
      get() {
        const modelsList = this.$store.getters['scoringProfilesModelsList/getModelsListToAssociate']
        return modelsList.map((el) => ({ value: el.id.toString(), text: el.name }))
      },
    },

    passThresholdPercent: {
      get() {
        return this.getPassThresholdPercent
      },
      set(newVal) {
        this.setPassThresholdPercent(newVal)
      },
    },

    // Selecting a model records its id (efficiencyTestsEditor.js's saveScoringProfile reads
    // getScoringProfileDefinitionSelectValue straight into the save payload's scoringProfileModelId)
    // and loads its driver weights for display in efficiency-tests-model-definition below — via
    // scoringProfilesModelsEditing/loadModelForLinking, the shared (not ScoringProfiles-specific)
    // "Scoring Profile Models" catalog lookup, same one scoringProfilesModelsList already reuses.
    scoringProfileDefinitionSelectValue: {
      get() {
        return this.getScoringProfileDefinitionSelectValue == null
          ? [-1]
          : [this.getScoringProfileDefinitionSelectValue.toString()]
      },
      set(newVal) {
        this.setScoringProfileDefinitionSelectValue(parseInt(newVal[0]))
        if (newVal[0] && parseInt(newVal[0]) !== 0) {
          this.onChangeModelType(parseInt(newVal[0]))
        } else {
          this.resetSelectedModel()
        }
      },
    },

    options: {
      get() {
        return [
          { value: 1, text: this.$t('efficiencyTests.geometricOption') },
          { value: 2, text: this.$t('efficiencyTests.customOption') },
          { value: 3, text: this.$t('efficiencyTests.linearOption') },
        ]
      },
    },

    userGuidelineText: {
      get() {
        return this.getSelectedWeightType === 2
          ? this.$t('efficiencyTests.dragDriversCustomGuideline')
          : this.$t('efficiencyTests.dragDriversGuideline')
      },
    },

    geometricDriversSequenceWeightsDecay: {
      get() {
        return this.getSequenceWeightsDecay
      },
      set(newVal) {
        this.setSequenceWeightsDecay(newVal)
        let list = [...this.getSelectedScoringDefinition.list]
        calculateDriversWeightsLists(
          null,
          list,
          this.getSelectedScoringDefinition.config,
          this.getSequenceWeightsDecay,
          this.setDriversList
        )
      },
    },

    isValidMaxTotalWeight: {
      get() {
        const maxWeight = this.getMaxTotalWeight
        const epsilon = this.getSelectedScoringDefinition.config === SCORING_DEFINITION_TYPES.custom ? 0.0001 : 0.0005
        return isNearlyEqual(maxWeight, 1, epsilon)
      },
    },
  },

  methods: {
    ...mapActions(['loadDriversIndicators', 'resetSelectedModel']),
    ...mapMutations([
      'setSelectedWeightType',
      'setSequenceWeightsDecay',
      'setDriversList',
      'setScoringProfileDefinitionSelectValue',
      'setSelectedModelId',
      'setSelectedModelName',
      'setSortedModelDrivers',
      'setUnsortedModelDrivers',
      'setSortedModelWeights',
      'setPassThresholdPercent',
    ]),

    onChangeWeights(val) {
      let sourceList = this.getSelectedScoringDefinition.list
      let targetList = this.getScoringDefinitionSettings[val].list

      if (this.getScoringDefinitionSettings[val].config === SCORING_DEFINITION_TYPES.custom && targetList.length > 0) {
        sourceList.forEach((j) => {
          const obj = targetList.find((k) => k.driver === j.driver)
          j.weight = obj ? obj.weight : j.weight
        })
      }

      this.setSelectedWeightType(val)
      calculateDriversWeightsLists(
        null,
        sourceList,
        this.getSelectedScoringDefinition.config,
        this.getSequenceWeightsDecay,
        this.setDriversList
      )
    },

    onRemoveAll() {
      const emptyList = []
      this.setDriversList(emptyList)
    },

    onRemoveDriver(value) {
      const list = [...this.getSelectedScoringDefinition.list].filter((item) => item.driver !== value)
      calculateDriversWeightsLists(
        null,
        list,
        this.getSelectedScoringDefinition.config,
        this.getSequenceWeightsDecay,
        this.setDriversList
      )
    },

    onAddDriver(value) {
      const list = [...this.getSelectedScoringDefinition.list]
      calculateDriversWeightsLists(
        value,
        list,
        this.getSelectedScoringDefinition.config,
        this.getSequenceWeightsDecay,
        this.setDriversList
      )
    },

    onUpdateCustomDriverWeight(name, value) {
      let list = [...this.getSelectedScoringDefinition.list]
      const index = list.findIndex((el) => el.driver === name)
      list[index].weight = formatDriverWeightNumber(value)
      this.setDriversList(list)
    },

    onUpdateDriversListSorting(value) {
      if (!Array.isArray(value)) return false
      const list = value.map((k) => {
        return typeof k === 'string' ? { driver: k, weight: 0 } : k
      })
      calculateDriversWeightsLists(
        null,
        list,
        this.getSelectedScoringDefinition.config,
        this.getSequenceWeightsDecay,
        this.setDriversList
      )
    },

    onChangeModelType(modelId) {
      this.resetSelectedModel()
      this.setSortedModelWeights([])
      this.setSelectedModelId(modelId)
      this.$store.dispatch('efficiencyTestsCommon/setLoading', true)
      this.$store
        .dispatch('scoringProfilesModelsEditing/loadModelForLinking', modelId)
        .then((modelInfo) => {
          this.setSelectedModelName(modelInfo.name)
          const result = mapScoringDefinitionforModel(modelInfo.scoringDefinition)
          this.setSortedModelWeights(result.sortedWeights)
          this.setUnsortedModelDrivers(result.driversList)
        })
        .catch((err) => {
          this.httpErrorsHandler(err)
        })
        .finally(() => {
          this.$store.dispatch('efficiencyTestsCommon/setLoading', false)
        })
    },

    onMoveDriver(item) {
      let list = this.getSortedModelDrivers
      let weigths = this.getSortedModelWeights
      if (!list.some((el) => el.driver === item.driver)) {
        list.push(item)
        list.map((el, index) => (el.weight = weigths[index]))
        this.setSortedModelDrivers(list)
      }
    },

    onResetSorting() {
      const emptyList = []
      this.setSortedModelDrivers(emptyList)
    },

    onUpdateSorting(value) {
      let weigths = this.getSortedModelWeights
      value.map((el, index) => (el.weight = weigths[index]))
      this.setSortedModelDrivers(value)
    },

    onRemoveModelDriver(item) {
      let list = this.getSortedModelDrivers
      let weigths = this.getSortedModelWeights
      list = list.filter((el) => el.driver !== item.driver)
      list.map((el, index) => (el.weight = weigths[index]))
      this.setSortedModelDrivers(list)
    },
  },

  created() {
    this.loadDriversIndicators()
  },
}
</script>

<style lang="scss" scoped>
.user-guideline-text {
  font-size: 0.9rem;
}

.sorting-btn {
  font-size: 0.8rem !important;
  text-decoration: none !important;
}
</style>
